import { lazy, Suspense, useEffect, type ComponentType } from 'react';
import { Navigate, useLocation, useParams, type RouteObject } from 'react-router';
import { Shell } from '../chrome/Shell';
import { ErrorScreen } from '../chrome/ErrorScreen';
import { NotFound } from '../chrome/NotFound';
import { ROUTED_SCREENS, RESUME_PATH, type ScreenId, type Stage } from './registry';
import { lazyScreen, type LazyScreen } from './lazyScreen';
/*
 * One chunk per stage (ux-flow.md): the check, the details, the claim, a fellow passenger's
 * page. A visit loads the stage it is in; the entry chunk holds the shell and the data layer.
 */
const stages = {
  eligibility: () => import('./stages/eligibility'),
  details: () => import('./stages/details'),
  compensation: () => import('./stages/compensation'),
  passenger: () => import('./stages/passenger'),
};
type Stages = typeof stages;
type StageModule<K extends keyof Stages> = Awaited<ReturnType<Stages[K]>>;

function screen<K extends keyof Stages>(stage: K, name: keyof StageModule<K>): LazyScreen {
  const load = stages[stage] as () => Promise<StageModule<K>>;
  return lazyScreen(load, (m) => m[name] as ComponentType);
}

/** Screen ID → its component (lazy, by stage). In-place screens (S3-manual, S11-handoff) are rendered by their host. */
export const SCREEN_COMPONENTS: Partial<Record<ScreenId, LazyScreen>> = {
  S0b: screen('eligibility', 'S0b'),
  S1: screen('eligibility', 'S1'),
  S2: screen('eligibility', 'S2'),
  S3: screen('eligibility', 'S3'),
  S4: screen('eligibility', 'S4'),
  S5: screen('eligibility', 'S5'),
  S6: screen('eligibility', 'S6'),
  'S6-stop': screen('eligibility', 'S6stop'),
  S7: screen('details', 'S7'),
  S8: screen('details', 'S8'),
  S9: screen('details', 'S9'),
  S10: screen('details', 'S10'),
  S11: screen('compensation', 'S11'),
  S12: screen('compensation', 'S12'),
  S13: screen('compensation', 'S13'),
  'S14-expired': screen('compensation', 'S14expired'),
  S15: screen('passenger', 'S15'),
};
const S15 = SCREEN_COMPONENTS.S15!;
const Resume = screen('compensation', 'Resume');

/** Loads every stage (tests, so screens render at once as they did before the split). */
export function preloadAllScreens(): Promise<unknown> {
  return Promise.all([...Object.values(SCREEN_COMPONENTS), Resume].map((c) => c.preload()));
}

/** What a visitor in a stage is likely to need next, fetched while the browser is idle. */
const NEXT_STAGE: Partial<Record<Stage, LazyScreen[]>> = {
  E: [SCREEN_COMPONENTS.S7!],
  D: [SCREEN_COMPONENTS.S11!],
};

export function prefetchAfter(stage: Stage | null) {
  const next = stage ? NEXT_STAGE[stage] : undefined;
  if (!next) return;
  const run = () => next.forEach((c) => void c.preload());
  if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 3000 });
  else setTimeout(run, 1500);
}

/** A routed screen; once it's up, the next stage's chunk is fetched while the browser is idle. */
function StageScreen({ C, stage }: { C: ComponentType; stage: Stage | null }) {
  useEffect(() => prefetchAfter(stage), [stage]);
  return <C />;
}

/** S15's URL segment is `p<n>` (ux-flow.md); anything else is not a passenger link. */
function S15Guard() {
  const { passenger } = useParams();
  return /^p[1-9]\d*$/.test(passenger ?? '') ? <S15 /> : <NotFound />;
}

/** /claim/ itself: the homepage's CTA goes to /claim/route; keep ?type= and any UTM params. */
function IndexRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/route${search}`} replace />;
}

// Dev-only gallery; import.meta.env.DEV is false in production, so the import is dropped.
const Gallery = import.meta.env.DEV ? lazy(() => import('../dev/Gallery')) : null;
const devRoutes: RouteObject[] = Gallery
  ? [
      {
        path: 'dev/gallery',
        element: (
          <Suspense fallback={null}>
            <Gallery />
          </Suspense>
        ),
        handle: { screen: 'dev-gallery' },
      },
    ]
  : [];

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <Shell />,
    errorElement: <ErrorScreen />,
    children: [
      { index: true, element: <IndexRedirect />, handle: { screen: 'index' } },
      ...ROUTED_SCREENS.map((def): RouteObject => {
        const C = def.id === 'S15' ? S15Guard : SCREEN_COMPONENTS[def.id];
        if (!C) throw new Error(`No component for ${def.id}`);
        return {
          path: def.path.slice(1),
          element: <StageScreen C={C} stage={def.stage} />,
          handle: { screen: def.id },
        };
      }),
      { path: RESUME_PATH.slice(1), element: <Resume />, handle: { screen: 'resume' } },
      ...devRoutes,
      { path: '*', element: <NotFound />, handle: { screen: 'not-found' } },
    ],
  },
];

export { BASENAME } from './registry';
